import React, { useState, useEffect } from 'react';
import api from '../api';
import {
  requestNotificationPermission,
  getNotificationPermission,
  sendBrowserNotification,
} from '../utils/browserNotification';

const CARD = {
  background: '#fff',
  borderRadius: 16,
  padding: 24,
  boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
  border: '1px solid #F1F3F5',
  marginBottom: 16,
};

const SECTION_TITLE = {
  fontSize: 16,
  fontWeight: 700,
  color: '#191F28',
  marginBottom: 4,
};

const SECTION_DESC = { fontSize: 13, color: '#8B95A1', marginBottom: 16 };

const INPUT = {
  width: '100%',
  padding: '12px 14px',
  border: '1px solid #E5E8EB',
  borderRadius: 10,
  fontSize: 14,
  color: '#191F28',
  outline: 'none',
  boxSizing: 'border-box',
};

const Toggle = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    onClick={() => !disabled && onChange(!checked)}
    disabled={disabled}
    style={{
      width: 44,
      height: 26,
      borderRadius: 999,
      border: 'none',
      background: checked ? '#3182F6' : '#D1D6DB',
      position: 'relative',
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'background 0.2s',
      opacity: disabled ? 0.5 : 1,
    }}
  >
    <span
      style={{
        position: 'absolute',
        top: 3,
        left: checked ? 21 : 3,
        width: 20,
        height: 20,
        borderRadius: '50%',
        background: '#fff',
        transition: 'left 0.2s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
      }}
    />
  </button>
);

const Row = ({ label, desc, children }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '14px 0',
      borderBottom: '1px solid #F1F3F5',
    }}
  >
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 14, color: '#191F28', fontWeight: 600 }}>{label}</div>
      {desc && <div style={{ fontSize: 12, color: '#8B95A1', marginTop: 2 }}>{desc}</div>}
    </div>
    <div>{children}</div>
  </div>
);

const NotificationSettingsPage = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [toast, setToast] = useState('');
  const [browserPermission, setBrowserPermission] = useState(() => getNotificationPermission());

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  };

  useEffect(() => {
    api
      .get('/admin/notifications/settings')
      .then((res) => setSettings(res.data?.settings || {}))
      .catch((err) => showToast(`불러오기 실패: ${err.response?.data?.error || err.message}`))
      .finally(() => setLoading(false));
  }, []);

  const update = (key, value) => {
    setSettings((s) => ({ ...s, [key]: value }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        email_enabled: !!settings.email_enabled,
        email_address: settings.email_address || '',
        push_enabled: !!settings.push_enabled,
        kakao_enabled: !!settings.kakao_enabled,
        kakao_phone: settings.kakao_phone || '',
        slack_webhook: settings.slack_webhook || '',
        notify_new_order: !!settings.notify_new_order,
        notify_cancellation: !!settings.notify_cancellation,
        notify_payment: !!settings.notify_payment,
        notify_settlement: !!settings.notify_settlement,
        notify_low_stock: !!settings.notify_low_stock,
      };
      const res = await api.put('/admin/notifications/settings', body);
      setSettings(res.data?.settings || settings);
      showToast('저장되었습니다');
    } catch (err) {
      showToast(`저장 실패: ${err.response?.data?.error || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const test = async (channel) => {
    setTesting(channel);
    try {
      const res = await api.post('/admin/notifications/test', { channel });
      const ok = res.data?.result?.ok;
      showToast(ok ? '테스트 알림 전송 성공' : `전송 실패: ${res.data?.result?.error || '확인 필요'}`);
    } catch (err) {
      showToast(`전송 실패: ${err.response?.data?.error || err.message}`);
    } finally {
      setTesting(null);
    }
  };

  const handleBrowserNotificationToggle = async (enabled) => {
    if (!enabled) {
      update('push_enabled', false);
      return;
    }
    if (browserPermission === 'unsupported') {
      showToast('이 브라우저는 알림을 지원하지 않습니다');
      return;
    }
    if (browserPermission === 'denied') {
      showToast('알림 권한이 거부되었습니다. 브라우저 설정에서 직접 허용해주세요.');
      return;
    }
    const result = await requestNotificationPermission();
    setBrowserPermission(result);
    if (result === 'granted') {
      update('push_enabled', true);
      showToast('브라우저 알림이 활성화되었습니다');
    } else {
      showToast('알림 권한이 거부되었습니다');
    }
  };

  const handleBrowserNotificationTest = () => {
    if (browserPermission !== 'granted') {
      showToast('먼저 알림 권한을 허용해주세요');
      return;
    }
    sendBrowserNotification('알림 테스트', '브라우저 알림이 정상적으로 동작합니다');
    showToast('테스트 알림을 전송했습니다');
  };

  const permissionLabel = {
    granted: '허용됨',
    denied: '거부됨',
    default: '미설정',
    unsupported: '미지원',
  };

  const permissionColor = {
    granted: '#2DB400',
    denied: '#F04452',
    default: '#8B95A1',
    unsupported: '#8B95A1',
  };

  if (loading) {
    return <div style={{ padding: 32, textAlign: 'center', color: '#8B95A1' }}>불러오는 중...</div>;
  }

  const TestBtn = ({ channel }) => (
    <button
      type="button"
      onClick={() => test(channel)}
      disabled={testing === channel}
      style={{
        padding: '8px 14px',
        background: '#F2F4F6',
        color: '#191F28',
        border: '1px solid #E5E8EB',
        borderRadius: 8,
        fontWeight: 600,
        fontSize: 12,
        cursor: testing === channel ? 'wait' : 'pointer',
        marginLeft: 8,
      }}
    >
      {testing === channel ? '전송 중' : '테스트'}
    </button>
  );

  return (
    <div style={{ padding: 32, background: '#F9FAFB', minHeight: '100vh' }}>
      <div style={{ maxWidth: 800, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 28, fontWeight: 800, color: '#191F28', margin: 0 }}>알림 설정</h1>
            <p style={{ fontSize: 14, color: '#8B95A1', margin: '4px 0 0' }}>
              어떤 이벤트에 어떤 채널로 알림을 받을지 설정합니다
            </p>
          </div>
          <button
            onClick={save}
            disabled={saving}
            style={{
              padding: '12px 24px',
              background: '#3182F6',
              color: '#fff',
              border: 'none',
              borderRadius: 10,
              fontWeight: 700,
              fontSize: 14,
              cursor: saving ? 'wait' : 'pointer',
            }}
          >
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>

        {/* Events */}
        <div style={CARD}>
          <div style={SECTION_TITLE}>알림받을 이벤트</div>
          <div style={SECTION_DESC}>어떤 상황에서 알림을 받을지 선택하세요</div>
          <Row label="신규 주문" desc="새로운 주문이 접수되었을 때">
            <Toggle checked={!!settings.notify_new_order} onChange={(v) => update('notify_new_order', v)} />
          </Row>
          <Row label="주문 취소" desc="고객이 주문을 취소했을 때">
            <Toggle checked={!!settings.notify_cancellation} onChange={(v) => update('notify_cancellation', v)} />
          </Row>
          <Row label="결제 완료" desc="결제가 정상적으로 완료되었을 때">
            <Toggle checked={!!settings.notify_payment} onChange={(v) => update('notify_payment', v)} />
          </Row>
          <Row label="정산 처리" desc="일일 정산이 완료되었을 때">
            <Toggle checked={!!settings.notify_settlement} onChange={(v) => update('notify_settlement', v)} />
          </Row>
          <Row label="재고 부족" desc="메뉴가 품절 위험에 있을 때">
            <Toggle checked={!!settings.notify_low_stock} onChange={(v) => update('notify_low_stock', v)} />
          </Row>
        </div>

        {/* Email */}
        <div style={CARD}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
            <div>
              <div style={SECTION_TITLE}>이메일 알림</div>
              <div style={SECTION_DESC}>지정한 이메일로 알림을 발송합니다</div>
            </div>
            <Toggle checked={!!settings.email_enabled} onChange={(v) => update('email_enabled', v)} />
          </div>
          <input
            type="email"
            placeholder="email@example.com"
            value={settings.email_address || ''}
            onChange={(e) => update('email_address', e.target.value)}
            style={{ ...INPUT, marginTop: 8 }}
          />
          <div style={{ marginTop: 12, textAlign: 'right' }}>
            <TestBtn channel="email" />
          </div>
        </div>

        {/* Slack */}
        <div style={CARD}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
            <div>
              <div style={SECTION_TITLE}>슬랙 알림</div>
              <div style={SECTION_DESC}>Slack Incoming Webhook으로 알림을 전송합니다</div>
            </div>
            <Toggle
              checked={!!settings.slack_webhook}
              onChange={(v) => update('slack_webhook', v ? settings.slack_webhook || '' : '')}
              disabled={false}
            />
          </div>
          <input
            type="url"
            placeholder="https://hooks.slack.com/services/..."
            value={settings.slack_webhook || ''}
            onChange={(e) => update('slack_webhook', e.target.value)}
            style={{ ...INPUT, marginTop: 8 }}
          />
          <div style={{ marginTop: 12, textAlign: 'right' }}>
            <TestBtn channel="slack" />
          </div>
        </div>

        {/* Browser Notification */}
        <div style={CARD}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
            <div>
              <div style={SECTION_TITLE}>브라우저 알림</div>
              <div style={SECTION_DESC}>이 기기의 브라우저로 즉시 알림을 받습니다 (새 주문, 직원 호출)</div>
            </div>
            <Toggle
              checked={!!settings.push_enabled && browserPermission === 'granted'}
              onChange={handleBrowserNotificationToggle}
            />
          </div>
          <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: '#8B95A1' }}>권한 상태:</span>
            <span
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: permissionColor[browserPermission] || '#8B95A1',
                background: browserPermission === 'granted' ? '#E8F8F0' : browserPermission === 'denied' ? '#FEE8EA' : '#F2F4F6',
                padding: '2px 10px',
                borderRadius: 20,
              }}
            >
              {permissionLabel[browserPermission] || '미설정'}
            </span>
          </div>
          <div style={{ marginTop: 12, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={handleBrowserNotificationTest}
              disabled={browserPermission !== 'granted'}
              style={{
                padding: '8px 14px',
                background: browserPermission === 'granted' ? '#F2F4F6' : '#F9FAFB',
                color: browserPermission === 'granted' ? '#191F28' : '#C4C9D1',
                border: '1px solid #E5E8EB',
                borderRadius: 8,
                fontWeight: 600,
                fontSize: 12,
                cursor: browserPermission === 'granted' ? 'pointer' : 'not-allowed',
              }}
            >
              테스트 알림
            </button>
          </div>
        </div>

        {/* Kakao */}
        <div style={CARD}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
            <div>
              <div style={SECTION_TITLE}>카카오 알림톡</div>
              <div style={SECTION_DESC}>카카오톡으로 알림톡을 발송합니다 (사전 등록 필요)</div>
            </div>
            <Toggle checked={!!settings.kakao_enabled} onChange={(v) => update('kakao_enabled', v)} />
          </div>
          <input
            type="tel"
            placeholder="010-0000-0000"
            value={settings.kakao_phone || ''}
            onChange={(e) => update('kakao_phone', e.target.value)}
            style={{ ...INPUT, marginTop: 8 }}
          />
          <div style={{ marginTop: 12, textAlign: 'right' }}>
            <TestBtn channel="kakao" />
          </div>
        </div>
      </div>

      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '12px 24px',
            background: '#191F28',
            color: '#fff',
            borderRadius: 10,
            fontSize: 14,
            fontWeight: 500,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 1000,
          }}
        >
          {toast}
        </div>
      )}
    </div>
  );
};

export default NotificationSettingsPage;
